import { type ChangeEvent, type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Event/ActionSettings/Snapshot/Snapshot.less";
import {
	Checkbox, Icon, IconSize, IconWeight, Input, type SelectOption, Popover, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { ActionState, ScheduleUpdater } from "Interfaces";
import { Link } from "react-router-dom";

const frequencyOptions: SelectOption[] = [ 0, 5, 10, 15, 30, 60, 120, 180, 300 ].map(value => ({
	label: value === 0 ? "Immediately" : `${ value } Sec`,
	value
}));

type SnapshotProps = {
	localActionState: [ ActionState, Dispatch<SetStateAction<ActionState>> ];
};

function Snapshot(props: SnapshotProps) {
	const { localActionState: [ localActionSettings, setLocalActionSettings ] } = props;
	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const handleSdReceiverUpdate = (value: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.snapshot.receiver = value;
		}));
	};

	const handleSdFilePrefixUpdate = (value: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.snapshot.filePrefix = value;
		}));
	};

	const handleSdFrequencySelect = (duration: string | null) => {
		if (!duration) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.snapshot.frequency = +duration;
		}));
	};

	const handleSdLogChange = (event: ChangeEvent<HTMLInputElement>) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.snapshot.log = event.currentTarget.checked;
		}));
	};

	const updateSdScheduleChecked = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.snapshot.schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const renderSchedule = (handleScheduleChange: ScheduleUpdater) => {
		return function Schedule(schedule: [ string, boolean ]) {
			const [ scheduleName, checked ] = schedule;

			return (
				<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
					<span>{ scheduleName }</span>
				</Checkbox>
			);
		};
	};

	const sdSchedule = Object
		.entries(localActionSettings.snapshot.schedule)
		.map(renderSchedule(updateSdScheduleChecked));

	return (
		<div className="snap-shot">
			<div className="column">
				<div className="page-title">
					<h4>SD Card</h4>
				</div>
				<div className="form-row">
					<span>Receiver</span>
					<Input
						value={ localActionSettings.snapshot.receiver }
						onUpdate={ handleSdReceiverUpdate }
					/>
				</div>
				<div className="form-row">
					<span>File Prefix</span>
					<Input
						value={ localActionSettings.snapshot.filePrefix }
						onUpdate={ handleSdFilePrefixUpdate }
					/>
				</div>
				<div className="form-row">
					<span>Min. Frequency</span>
					<Select
						allowClear={ false }
						onSelect={ handleSdFrequencySelect }
						options={ frequencyOptions }
						value={ localActionSettings.snapshot.frequency }
					/>
				</div>
				<div className="form-row">
					<span>Log</span>
					<Checkbox
						checked={ localActionSettings.snapshot.log }
						className="form-input"
						onChange={ handleSdLogChange }
					/>
				</div>
			</div>
			<div className="column">
				<div className="schedule-title">
					<h4>Schedule</h4>
					<Popover content={ link }>
						<Icon name="circle-info" iconWeight={ IconWeight.REGULAR } size={ IconSize.SMALLER } />
					</Popover>
				</div>
				<div className="schedules">
					{ sdSchedule }
				</div>
			</div>
		</div>
	);
}

export default memo(Snapshot);
